import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { APOLLO_LOGO, APOLLO_TAGLINE } from "@/lib/brand";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    const r = await login(location);
    setBusy(false);
    if (!r.ok) setError(r.error);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-white noise" data-testid="login-page">
      {/* Left brand pane */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/10 relative overflow-hidden">
        <div className="flex flex-col items-start gap-2">
          <img src={APOLLO_LOGO} alt="Apollo" className="h-10 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Brand Command</span>
        </div>

        <div className="max-w-md relative z-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Mission 01 — Access</div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
            Engineered<br /> for <span className="text-[#FBC90C]">elevation</span>.
          </h1>
          <p className="text-white/60 mt-6 text-sm max-w-sm leading-relaxed">
            Mission driven. Purpose built. Performance delivered. Every logo, product, menu, and campaign — assembled in a single, precise portal.
          </p>
          <div className="mt-10 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FBC90C]" />
            <span className="font-display text-xs uppercase tracking-[0.4em] text-white/80">{APOLLO_TAGLINE}</span>
          </div>
        </div>

        <div className="flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          <span>CAN · MA · NY</span>
          <span>·</span>
          <span>V 1.0 — 2026</span>
        </div>

        {/* Brand accent bars */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-3 pr-8 opacity-40 pointer-events-none">
          <div className="w-1 h-24 bg-[#E15C2C]" />
          <div className="w-1 h-24 bg-[#FBC90C]" />
          <div className="w-1 h-24 bg-[#B02065]" />
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex flex-col items-start gap-2">
            <img src={APOLLO_LOGO} alt="Apollo" className="h-10 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Brand Command</span>
          </div>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Sign In</div>
                <h2 className="font-display text-3xl uppercase font-bold tracking-tight">Welcome back</h2>
                <p className="text-sm text-white/50 mt-2">Enter your location initial to access your assets.</p>
              </div>

              <Field label="Location Initial">
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                  autoFocus
                  autoCapitalize="characters"
                  autoComplete="off"
                  placeholder="e.g. NY, NYT, MA, CAN"
                  data-testid="login-location"
                  className="w-full bg-transparent border-b border-white/20 py-2 text-sm uppercase outline-none focus:border-white transition placeholder:normal-case placeholder:text-white/30"
                />
              </Field>

              {error && <div className="text-xs text-[#E15C2C]" data-testid="login-error">{error}</div>}

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-white text-black font-semibold py-3 uppercase tracking-widest text-xs hover:bg-white/90 transition flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-submit"
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Enter Portal
              </button>


            </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
